import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marketplaceAPI } from '../lib/api';
import { BarChart3, TrendingUp, Globe, Users, Gavel, DollarSign, Loader2, ArrowRight, Layers, Hash } from 'lucide-react';

export default function MarketplaceAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketplaceAPI.analytics()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;
  if (!data) return <div className="text-center py-32 text-zinc-500">Failed to load analytics</div>;

  const { overview, market_value, categories, tlds, price_distribution, length_distribution, trending_domains, recently_listed } = data;

  const maxCatCount = Math.max(...(categories.map(c => c.count) || [1]));
  const maxTldCount = Math.max(...(tlds.map(t => t.count) || [1]));
  const maxPriceCount = Math.max(...(price_distribution.map(p => p.count) || [1]));
  const maxLenCount = Math.max(...(length_distribution.map(l => l.count) || [1]));

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="marketplace-analytics-page">
      {/* Hero */}
      <div className="text-center mb-16">
        <BarChart3 className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
        <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Outfit'] tracking-tight">Marketplace Analytics</h1>
        <p className="text-zinc-500 mt-3 max-w-xl mx-auto text-base">Real-time insights into the XDomainX domain marketplace. Explore trends, pricing, and opportunities.</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { icon: Globe, label: 'Active Domains', value: overview.total_domains.toLocaleString(), color: 'text-[#00E5FF]', border: 'border-[#00E5FF]/20' },
          { icon: Gavel, label: 'Live Auctions', value: overview.total_auctions.toLocaleString(), color: 'text-amber-400', border: 'border-amber-500/20' },
          { icon: Users, label: 'Registered Users', value: overview.total_users.toLocaleString(), color: 'text-purple-400', border: 'border-purple-500/20' },
          { icon: DollarSign, label: 'Total Market Value', value: `$${(market_value.total / 1000).toFixed(0)}K`, color: 'text-[#00FF94]', border: 'border-[#00FF94]/20' },
        ].map((s, i) => (
          <div key={i} className={`p-5 bg-[#0A0A0A] border ${s.border} rounded-sm`} data-testid={`stat-${i}`}>
            <s.icon className={`h-5 w-5 ${s.color} mb-3`} />
            <div className="text-2xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Market Value Summary */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 mb-12" data-testid="market-value-section">
        <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-[#00FF94]" /> Market Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Average Price', value: `$${market_value.average.toLocaleString(undefined, {maximumFractionDigits: 0})}` },
            { label: 'Lowest Price', value: `$${market_value.min.toLocaleString(undefined, {maximumFractionDigits: 0})}` },
            { label: 'Highest Price', value: `$${market_value.max.toLocaleString(undefined, {maximumFractionDigits: 0})}` },
            { label: 'New This Month', value: overview.new_domains_30d },
          ].map((s, i) => (
            <div key={i} className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</div>
              <div className="text-lg font-bold text-white font-['Outfit'] mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Category Distribution */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="category-distribution">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#00E5FF]" /> Categories
          </h2>
          <div className="space-y-3">
            {categories.slice(0, 8).map((c, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white capitalize">{c.name}</span>
                  <span className="text-xs text-zinc-500">{c.count} domains &middot; avg ${c.avg_price.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                </div>
                <div className="h-2 bg-[#050505] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#00E5FF] to-[#00E5FF]/40 transition-all duration-700"
                    style={{ width: `${(c.count / maxCatCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TLD Distribution */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="tld-distribution">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-amber-400" /> TLD Distribution
          </h2>
          <div className="space-y-3">
            {tlds.slice(0, 8).map((t, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white font-mono">{t.tld}</span>
                  <span className="text-xs text-zinc-500">{t.count} domains &middot; avg ${t.avg_price.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                </div>
                <div className="h-2 bg-[#050505] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-400/40 transition-all duration-700"
                    style={{ width: `${(t.count / maxTldCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price Distribution */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="price-distribution">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#00FF94]" /> Price Ranges
          </h2>
          <div className="space-y-3">
            {price_distribution.map((p, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white">{p.label}</span>
                  <span className="text-xs text-zinc-500">{p.count} domains</span>
                </div>
                <div className="h-2 bg-[#050505] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#00FF94] to-[#00FF94]/40 transition-all duration-700"
                    style={{ width: `${maxPriceCount > 0 ? (p.count / maxPriceCount) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Domain Length Distribution */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="length-distribution">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <Hash className="h-5 w-5 text-purple-400" /> Domain Length
          </h2>
          {length_distribution.length > 0 ? (
            <div className="space-y-3">
              {length_distribution.map((l, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white">{l.label}</span>
                    <span className="text-xs text-zinc-500">{l.count} domains &middot; avg ${l.avg_price.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  </div>
                  <div className="h-2 bg-[#050505] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-purple-400 to-purple-400/40 transition-all duration-700"
                      style={{ width: `${maxLenCount > 0 ? (l.count / maxLenCount) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">Not enough data</p>
          )}
        </div>
      </div>

      {/* Trending & Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Trending Domains */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="trending-domains">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-red-400" /> Trending Domains
          </h2>
          {trending_domains.length > 0 ? (
            <div className="space-y-2">
              {trending_domains.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-600 w-5 text-right">{i + 1}</span>
                    <span className="text-sm text-white font-medium">{d.full_name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-zinc-500">{d.bid_count} bids</span>
                    <span className="text-sm text-[#00E5FF] font-bold">${(d.current_bid || d.price)?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No active bids yet</p>
          )}
        </div>

        {/* Recently Listed */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="recently-listed">
          <h2 className="text-base md:text-lg font-bold text-white font-['Outfit'] mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-[#00E5FF]" /> Recently Listed
          </h2>
          {recently_listed.length > 0 ? (
            <div className="space-y-2">
              {recently_listed.map((d, i) => (
                <Link key={i} to={`/domains/${d.id}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/[0.02] -mx-2 px-2 rounded-sm transition-colors">
                  <div>
                    <span className="text-sm text-white font-medium">{d.full_name}</span>
                    <span className="ml-2 text-xs text-zinc-600 capitalize">{d.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-sm ${d.is_auction ? 'bg-amber-500/10 text-amber-400' : 'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>
                      {d.is_auction ? 'Auction' : 'Buy Now'}
                    </span>
                    <span className="text-sm text-white font-bold">${d.price?.toLocaleString()}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No domains listed yet</p>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-12 bg-[#0A0A0A] border border-white/5 rounded-sm" data-testid="analytics-cta">
        <h2 className="text-xl font-bold text-white font-['Outfit'] mb-3">Ready to Find Your Perfect Domain?</h2>
        <p className="text-zinc-500 mb-6">Browse our marketplace or use advanced search to discover domains that match your needs.</p>
        <div className="flex justify-center gap-3">
          <Link to="/domains">
            <button className="h-11 px-6 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm text-sm transition-all" data-testid="browse-domains-cta">
              Browse Domains <ArrowRight className="ml-2 h-4 w-4 inline" />
            </button>
          </Link>
          <Link to="/search">
            <button className="h-11 px-6 border border-zinc-700 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] font-bold uppercase tracking-wider rounded-sm text-sm transition-all" data-testid="advanced-search-cta">
              Advanced Search
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
